import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, FileCheck, MapPin, Calendar, User, Package, Shirt, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import PrintEntregaButtons from './PrintEntregaButtons';

const VerComprobanteModal = ({ isOpen, onClose, solicitud }) => {
  if (!solicitud) return null;

  const getTotalItems = () => {
    return solicitud.items?.reduce((sum, i) => sum + (i.cantidad || 0), 0) || 0;
  };

  const getTotalKilos = () => {
    if (solicitud.tipo !== 'lenceria') return null;
    const totalGramos = solicitud.items?.reduce((sum, i) => {
      const peso = parseFloat(i.peso) || 0;
      const cantidad = i.cantidad || 0;
      return sum + (peso * cantidad);
    }, 0) || 0;
    const totalKg = totalGramos / 1000;
    return totalKg.toFixed(2) ;
  };

  const formatFecha = (fecha) => {
    if (!fecha) return 'N/A';
    return new Date(fecha).toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                    <FileCheck className="h-6 w-6 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                      Comprobante de Entrega
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {solicitud.tipo === 'lenceria' ? 'Lencería' : 'Insumos'}
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onClose}
                  className="rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
                {/* Información de la entrega */}
                <div className="space-y-4 mb-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                      <MapPin className="h-5 w-5 text-blue-600 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Edificio</p>
                        <p className="font-semibold text-sm">{solicitud.edificio?.nombre}</p>
                        <p className="text-xs text-gray-600">{solicitud.edificio?.ciudad}</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                      <Home className="h-5 w-5 text-purple-600 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Activo</p>
                        <p className="font-semibold text-sm">{solicitud.displayName || solicitud.hospedaje?.nombre}</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                      <Calendar className="h-5 w-5 text-green-600 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Fecha de Entrega</p>
                        <p className="font-semibold text-sm">{formatFecha(solicitud.fechaEntrega)}</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                      <User className="h-5 w-5 text-orange-600 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Entregado por</p>
                        <p className="font-semibold text-sm">
                          {solicitud.entregadoPor?.nombre || solicitud.conductor?.nombre || 'Conductor'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Receptor */}
                  {solicitud.receptor_nombre && (
                    <div className="flex items-start space-x-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                      <User className="h-5 w-5 text-green-600 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Recibido por</p>
                        <p className="font-semibold text-sm text-green-700 dark:text-green-400">
                          {solicitud.receptor_nombre}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Observaciones */}
                  {solicitud.observaciones && (
                    <div className="col-span-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Observaciones</p>
                      <p className="text-sm text-gray-900 dark:text-gray-100">
                        {solicitud.observaciones}
                      </p>
                    </div>
                  )}
                </div>

                {/* Items entregados */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-lg flex items-center gap-2">
                      {solicitud.tipo === 'lenceria' ? (
                        <Shirt className="h-5 w-5 text-blue-600" />
                      ) : (
                        <Package className="h-5 w-5 text-purple-600" />
                      )}
                      {solicitud.tipo === 'lenceria' ? 'Prendas Entregadas' : 'Insumos Entregados'}
                    </h3>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        {getTotalItems()} items
                      </Badge>
                      {getTotalKilos() && (
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                          {getTotalKilos()} kg
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-50 dark:bg-gray-900">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-400">
                            Item
                          </th>
                          {solicitud.tipo === 'lenceria' && (
                            <th className="px-4 py-2 text-center text-xs font-medium text-gray-600 dark:text-gray-400">
                              Peso (kg)
                            </th>
                          )}
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 dark:text-gray-400">
                            Cantidad
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {solicitud.items?.map((item, idx) => (
                          <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-900">
                            <td className="px-4 py-3 text-sm">{item.nombre}</td>
                            {solicitud.tipo === 'lenceria' && (
                              <td className="px-4 py-3 text-sm text-center text-gray-600">
                                {item.peso ? `${item.peso} kg` : '-'}
                              </td>
                            )}
                            <td className="px-4 py-3 text-sm text-right font-semibold">{item.cantidad}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Firma */}
                {(solicitud.firmaDigital || solicitud.firma) && (
                  <div className="mb-6">
                    <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                      <FileCheck className="h-5 w-5 text-green-600" />
                      Firma del Receptor
                    </h3>
                    <div className="border-2 border-green-200 dark:border-green-800 rounded-lg p-4 bg-gradient-to-br from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20">
                      <img 
                        src={solicitud.firmaDigital || solicitud.firma} 
                        alt="Firma del receptor" 
                        className="max-w-full h-40 mx-auto object-contain bg-white dark:bg-gray-800 rounded p-2"
                        onError={(e) => {
                          const firmaUrl = solicitud.firmaDigital || solicitud.firma;
                          console.error('Error al cargar firma:', firmaUrl);
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = `
                            <div class="text-center text-red-600 dark:text-red-400 p-4">
                              <p class="font-semibold text-lg mb-2">⚠️ Error al cargar la firma</p>
                              <p class="text-sm mt-2 break-all">URL: ${firmaUrl}</p>
                              <p class="text-xs mt-2 text-gray-600 dark:text-gray-400">
                                Posibles causas:<br/>
                                • El bucket de R2 no tiene acceso público habilitado<br/>
                                • La URL de la firma es incorrecta<br/>
                                • El archivo fue eliminado
                              </p>
                            </div>
                          `;
                        }}
                        onLoad={() => {
                          const firmaUrl = solicitud.firmaDigital || solicitud.firma;
                          console.log('✅ Firma cargada correctamente:', firmaUrl);
                        }}
                      />
                      <p className="text-center text-xs text-gray-600 dark:text-gray-400 mt-2">
                        {solicitud.receptor_nombre && `Firmado por: ${solicitud.receptor_nombre}`}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between gap-3 p-6 border-t bg-gray-50 dark:bg-gray-900">
                <PrintEntregaButtons 
                  entrega={solicitud}
                  disabled={false}
                />
                <Button
                  variant="outline"
                  onClick={onClose}
                >
                  Cerrar
                </Button>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default VerComprobanteModal;
